<template>
  <div class="loan-term-configuration">
    <h1 class="tw-font-bold overview">Overview</h1>
    <div class="header">
      <div>
        <h1 class="">Loan Term Configuration</h1>
        <p>
          Loan configuration involves setting the key parameters of a loan, such
          as the loan amount, interest rate, repayment term, and payment
          schedule.
        </p>
      </div>
      <router-link
        :to="{ name: 'LoanConfiguration' }"
        class="setup-button tw-block"
      >
        Set-up Loan Configuration
        <svg
          width="6"
          height="9"
          viewBox="0 0 6 9"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.17202 4.49984L0.343018 1.67184L1.75702 0.256836L6.00002 4.49984L1.75702 8.74284L0.343018 7.32784L3.17202 4.49984Z"
            fill="white"
          />
        </svg>
      </router-link>
    </div>

    <div class="loan-summary-container">
      <div class="summary-heading">
        <h2>Loan Summary</h2>
        <CurrencyToggle :disabled="summaryLoading" @change="onCurrencyChange" />
      </div>

      <div class="loan-summary">
        <div class="summary-card">
          <div>
            <h3 class="tw-text-sm tw-font-medium tw-text-[#4C5B90]">
              Loans Disbursed
            </h3>
            <p class="summary-number tw-text-black">
              {{ loanSummary?.totalLoans || 0 }}
            </p>
          </div>
          <p class="summary-detail tw-text-sm tw-font-medium tw-text-[#4C5B90]">
            Total Loans Disbursed:
            <span class="tw-text-base tw-text-[#2A62F5] tw-font-bold">{{
              formattedDisbursedAmount
            }}</span>
          </p>
        </div>
        <div class="summary-card">
          <div>
            <h3 class="tw-text-sm tw-font-medium tw-text-[#4C5B90]">
              Total Borrowers
            </h3>
            <p class="summary-number tw-text-black">
              {{ loanSummary?.totalBorrowers || 0 }}
            </p>
          </div>
          <p class="summary-detail tw-text-sm tw-font-medium tw-text-[#4C5B90]">
            Repay Rate:
            <span class="tw-text-base tw-text-[#2A62F5] tw-font-bold">{{
              formattedRepayRate
            }}</span>
          </p>
        </div>
        <div class="summary-card">
          <div>
            <h3 class="tw-text-sm tw-font-medium tw-text-[#4C5B90]">
              Active Loans
            </h3>
            <p class="summary-number tw-text-black">
              {{ activeLoansCount }}
            </p>
          </div>
          <p class="summary-detail tw-text-sm tw-font-medium tw-text-[#4C5B90]">
            Share of Loans:
            <span class="tw-text-base tw-text-[#2A62F5] tw-font-bold">{{
              shareOfLoans(activeLoansCount)
            }}</span>
          </p>
        </div>
        <div class="summary-card">
          <div>
            <h3 class="tw-text-sm tw-font-medium tw-text-[#4C5B90]">
              Repaid Loans
            </h3>
            <p class="summary-number tw-text-black">
              {{ repaidLoansCount }}
            </p>
          </div>
          <p class="summary-detail tw-text-sm tw-font-medium tw-text-[#4C5B90]">
            Total Repaid Amount:
            <span class="tw-text-base tw-text-[#2A62F5] tw-font-bold">{{
              formattedRepaidAmount
            }}</span>
          </p>
        </div>
        <div class="summary-card">
          <div>
            <h3 class="tw-text-sm tw-font-medium tw-text-[#4C5B90]">
              Liquidated Loans
            </h3>
            <p class="summary-number tw-text-black">
              {{ liquidatedLoansCount }}
            </p>
          </div>
          <p class="summary-detail tw-text-sm tw-font-medium tw-text-[#4C5B90]">
            Share of Loans:
            <span class="tw-text-base tw-text-[#2A62F5] tw-font-bold">{{
              shareOfLoans(liquidatedLoansCount)
            }}</span>
          </p>
        </div>
      </div>
    </div>

    <div v-if="!isLoading && borrowers?.length > 0">
      <LoansTable :borrowers="borrowers" :totalBorrowers="loanSummary?.totalBorrowers" />
      <router-link
        class="tw-text-primary tw-font-medium tw-block tw-mt-4 tw-text-right"
        :to="{ name: 'CreditBorrowersList' }"
        >See all borrowers ></router-link
      >
    </div>
    <div
      class="tw-bg-white tw-h-64 tw-flex tw-items-center tw-justify-center tw-mt-12"
      v-else
    >
      <EmptyState name="credit-provider">
        <template v-slot:message>
          <div class="tw-max-w-sm">
            <p
              class="tw-font-bold tw-text-lg tw-mb-2 tw-text-secondary tw-tracking-[-0.1px] tw-font-medium tw-text-center tw-mt-5"
            >
              Nothing to see here, yet.
            </p>
            <p
              class="message tw-text-center tw-text-[#4C5B90] tw-my-3 tw-text-sm tw-font-light"
            >
              No loans requested yet.
            </p>
          </div>
        </template>
      </EmptyState>
    </div>

    <div v-if="!isLoading && loans?.length > 0">
      <CreditLoansTable :loans="loans" subtitle="Most recent loans" />
      <router-link
        class="tw-text-primary tw-font-medium tw-block tw-mt-4 tw-text-right"
        :to="{ name: 'CreditLoans' }"
        >See all loans ></router-link
      >
    </div>

    <section class="tw-mt-10">
      <div
        class="tw-flex tw-flex-wrap tw-gap-y-3 tw-w-full tw-justify-between tw-mb-3"
      >
        <h2 class="tw-font-bold tw-text-lg tw-text-black-dark">
          Recent Transactions
        </h2>
      </div>
      <div
        class="table tw-bg-white tw-overflow-x-scroll"
        v-if="transactions && transactions.length > 1"
      >
        <TransactionTable
          :tableHeaders="tableHeaders"
          :tableRows="tableRows"
          :columnNumber="1"
          :columnWidth="100"
          :showPagination="false"
          :totalPage="TransactionPages"
          :currentPage="TransactionPage"
          @go-to-page="gotoPage"
        />

        <router-link
          class="tw-text-primary tw-font-medium tw-block tw-mb-8 md:tw-mr-8 tw-text-right"
          :to="{ name: 'Wallet' }"
          >See all transactions ></router-link
        >
      </div>
      <div
        class="tw-bg-white tw-h-64 tw-flex tw-items-center tw-justify-center"
        v-else
      >
        <EmptyState name="transaction">
          <template v-slot:message>
            <div class="tw-max-w-sm">
              <p
                class="tw-font-bold tw-text-lg tw-mb-2 tw-text-secondary tw-tracking-[-0.1px] tw-font-medium tw-text-center tw-mt-5"
              >
                Nothing to see here, yet.
              </p>
              <p
                class="message tw-text-center tw-text-[#4C5B90] tw-my-3 tw-text-sm tw-font-light"
              >
                Orders will show up here when someone buys or sells your tokens.
              </p>
            </div>
          </template>
        </EmptyState>
      </div>
    </section>
  </div>
</template>
  
<script>
import CurrencyToggle from "@/components/creditProvider/CurrencyToggle.vue";
import LoansTable from "@/components/tables/creditProvider/LoansTable.vue";
import CreditLoansTable from "@/components/tables/creditProvider/CreditLoansTable.vue";
import { mapActions, mapGetters } from "vuex";
import TransactionTable from "@/components/tables/TransactionTable";
import EmptyState from "@/components/notification/EmptyState";
import {
  convertToLocale,
  formatAmountToDollar,
  formatISODate,
  formatISOTime,
  removeUnderscore,
} from "@/utils/helpers";

export default {
  name: "Credit Provider Dashboard",
  components: {
    CurrencyToggle,
    LoansTable,
    CreditLoansTable,
    TransactionTable,
    EmptyState,
  },
  data() {
    return {
      tableHeaders: ["", "Details", "Reference", "Amount", "Status", "misc"],
      tableRows: [],
      isLoading: true,
      hasError: false,
      summaryLoading: false,
    };
  },
  computed: {
    ...mapGetters("organisationModule", [
      "creditCurrency",
      "loans",
      "borrowers",
      "loanSummary",
      "transactions",
      "TransactionPage",
      "TransactionPages",
    ]),

    formattedRepayRate() {
      const rate = this.loanSummary?.repayRate;
      return rate ? `${rate.toFixed(2)}%` : "0%";
    },

    activeLoansCount() {
      return this.statusCount("totalActiveLoans", "ACTIVE");
    },

    repaidLoansCount() {
      return this.statusCount("totalRepaidLoans", "COMPLETED");
    },

    liquidatedLoansCount() {
      return this.statusCount("totalLiquidatedLoans", "LIQUIDATED");
    },

    formattedRepaidAmount() {
      return this.formatMoney(this.loanSummary.totalRepaidAmount);
    },

    formattedDisbursedAmount() {
      return this.formatMoney(this.loanSummary.totalDisbursedAmount);
    },
  },
  created() {
    this.fetchAllData();
  },
  methods: {
    ...mapActions("organisationModule", [
      "getLoans",
      "getBorrowers",
      "getLoanSummary",
      "getTransactions",
    ]),

    async fetchAllData() {
      this.isLoading = true;
      this.hasError = false;

      try {
        await Promise.all([
          this.getBorrowers({ page: 1, limit: 5 }),
          this.getLoanSummary(),
          this.getLoans({ page: 1, limit: 10 }),
          this.getTransactions({ page: 1, limit: 5 }),
        ]);
      } catch (error) {
        this.hasError = true;
        console.error("Error fetching data:", error);
      } finally {
        this.isLoading = false;
      }
    },

    // The summary endpoint is the source of truth for the status breakdown.
    // Fall back to counting the loans we already hold so the cards still read
    // correctly against an API that predates these fields.
    formatMoney(amount) {
      return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: this.creditCurrency,
        minimumFractionDigits: 2,
      }).format(Number(amount) || 0);
    },

    // The scope is dashboard-wide, so the tables reload with the summary rather
    // than leaving a mixed-currency list beside single-currency totals.
    async onCurrencyChange() {
      this.summaryLoading = true;
      try {
        await this.fetchAllData();
      } finally {
        this.summaryLoading = false;
      }
    },

    statusCount(summaryKey, status) {
      const fromSummary = this.loanSummary?.[summaryKey];
      if (typeof fromSummary === "number") return fromSummary;
      return (this.loans || []).filter(
        (loan) =>
          (loan.status || "").toUpperCase() === status &&
          (loan.currency || "").toUpperCase() === this.creditCurrency
      ).length;
    },

    shareOfLoans(count) {
      const total = Number(this.loanSummary?.totalLoans) || 0;
      if (!total) return "0%";
      return `${((count / total) * 100).toFixed(0)}%`;
    },

    async gotoPage(page) {
      const query = { page: page, limit: 5, status: this.status };
      try {
        await this.getTransactions(query);
      } catch (error) {
        responseHandler.handleError(error);
      }
    },

    updateTableRow() {
      this.tableRows = this.transactions.map((transaction) => {
        let type = removeUnderscore(transaction.type);
        switch (type) {
          case "Debit Sell Wallet":
            type = "Debit Wallet";
            break;

          case "Fund Sell Wallet":
            type = "Fund Wallet";
            break;
          default:
            break;
        }

        let name = `${transaction.token.name}`;
        switch (name) {
          case "GetEquity USD Token":
            name = "USD";
            break;
          default:
            break;
        }
        let volume = transaction.volume;
        let details;
        if (type === "Plan") {
          details = `${type}: ${transaction.price} ${name}`;
        } else {
          details = `${type}: ${volume} ${name}`;
        }
        let amount;
        if (transaction.price) {
          amount = transaction.price * transaction.volume;
        } else {
          amount = transaction.token?.price.buy * transaction.volume;
        }
        return {
          details: `<span class="tw-font-bold">${details}</span> <br />
          <span class="tw-text-black-light tw-text-xs">${formatISODate(
            transaction.createdAt
          )}  ${formatISOTime(transaction.createdAt)}</span>`,
          reference:
            type === "Fee" ||
            type === "Debit Wallet" ||
            type === "Send" ||
            type === "Withdraw" ||
            type === "Sell" ||
            type === "Plan"
              ? `<span class="tw-text-xs tw-mb-1">${transaction.reference}</span> <br />
          <span class="tw-text-xs tw-text-error">Debit</span>`
              : `<span class="tw-text-xs tw-mb-1">${transaction.reference}</span> <br />
          <span class="tw-text-xs tw-text-success">Credit</span>`,
          amount: formatAmountToDollar(
            amount,
            2,
            transaction.currency || transaction.token?.currency || "USD"
          ),
          // type: `<span class="tw-hidden">${type}</span>`,
          // image: `<span class="tw-hidden">${transaction.token?.image}</span>`,
          status: transaction.status,
          misc: `misc/${transaction._id}/${type}`,
        };
      });
    },
  },

  watch: {
    transactions(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updateTableRow();
      }
    },
  },
};
</script>
  
  <style scoped>
.loan-term-configuration {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
}

.overview {
  font-size: 22px;
  color: #1a2139;
  width: fit-content;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px;
  margin: 30px 0px;
  background: #fff;
  border: 2px solid #f0f4fd;
  border-radius: 8px;
}

.header div {
  width: 60%;
}

@media (max-width: 600px) {
  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }

  .header div {
    width: 90%;
  }
}

.header div h1 {
  font-weight: 800;
  font-size: 20px;
  margin-bottom: 10px;
}

@media (max-width: 600px) {
  .header div h1 {
    font-size: 18px;
  }
}

.header div p {
  color: #8d9da8;
  width: 100%;
  font-weight: 100;
  font-size: 14px;
}

.setup-button {
  background-color: #4d84ff;
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 5px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: fit-content;
  gap: 8px;
}

@media (max-width: 600px) {
  .setup-button {
    padding: 8px 12px;
    font-size: 14px;
  }
}

.loan-summary-container {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  padding: 30px;
}

.summary-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.loan-summary-container h2 {
  font-weight: 700;
  font-size: 18px;
  color: #2d303d;
}

.loan-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.summary-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid #e2e8f0;
  padding: 15px;
  text-align: left;
  border-radius: 6px;
}

.summary-number {
  font-size: 30px;
  font-weight: bold;
  margin-bottom: 10px;
}

.summary-detail {
  border-top: 1px solid #f1f5f9;
  padding: 5px 0px;
}

@media (max-width: 480px) {
  .loan-summary {
    grid-template-columns: 1fr;
  }
}
</style>